import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useImpersonation } from './ImpersonationContext';
import { ConfirmDialog } from '../components/ui';
import {
  confirmLiveWrite,
  LiveWriteConfirmHandler,
  setLiveWriteConfirmHandler,
} from '../utils/impersonationWriteGate';

/**
 * Asks the admin to confirm every live write made while viewing as another
 * account (MOT-169), from one place.
 *
 * The provider registers the dialog with `utils/impersonationWriteGate`, which
 * `makeApiCall` (fetch) calls directly and which is attached to axios here, so
 * Invite Member, Product QA, Create New Key, MAP and rebate imports, Dealer
 * Pricing upload and every other mutating call are covered without touching
 * the pages. Mounted by `Layout`, so it covers every `/app/*` route.
 */

// Axios is used directly by `apiService` (API keys, MAP pricing, integrations ...).
// Attached once at import time so it is in place before any page effect can fire
// a request; with no provider mounted the gate lets everything through.
axios.interceptors.request.use(async (config) => {
  await confirmLiveWrite(config.method, config.url);
  return config;
});

interface PendingConfirmation {
  resolve: (approved: boolean) => void;
}

const FALLBACK_ACCOUNT_NAME = 'this account';

/**
 * For code that wants to confirm before doing something that is not an HTTP
 * request (for example a direct Firestore write). Resolves true to proceed.
 * Outside impersonation it resolves true immediately.
 */
export const useConfirmLiveWrite = (): (() => Promise<boolean>) =>
  useCallback(async () => {
    try {
      await confirmLiveWrite('POST', '');
      return true;
    } catch {
      return false;
    }
  }, []);

export const ImpersonationWriteGuardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isImpersonating, impersonatedOrg, impersonatedBrand } = useImpersonation();
  const [pending, setPending] = useState<PendingConfirmation | null>(null);
  const pendingRef = useRef<PendingConfirmation | null>(null);
  pendingRef.current = pending;

  const accountName = impersonatedOrg?.name || impersonatedBrand?.name || FALLBACK_ACCOUNT_NAME;

  const request = useCallback<LiveWriteConfirmHandler>(
    () =>
      new Promise<boolean>((resolve) => {
        // A dialog is already open: settle it as declined rather than leak it.
        pendingRef.current?.resolve(false);
        setPending({ resolve });
      }),
    []
  );

  // A layout effect, so the handler is registered before any child effect can
  // issue a request.
  useLayoutEffect(() => {
    const unregister = setLiveWriteConfirmHandler(request);
    return () => {
      unregister();
      pendingRef.current?.resolve(false);
    };
  }, [request]);

  // Leaving impersonation with a dialog open must not leave a write waiting.
  useEffect(() => {
    if (pendingRef.current) {
      pendingRef.current.resolve(false);
      setPending(null);
    }
  }, [isImpersonating, impersonatedOrg?.id, impersonatedBrand?.id]);

  const settle = (approved: boolean) => {
    pending?.resolve(approved);
    setPending(null);
  };

  return (
    <>
      {children}
      <ConfirmDialog
        open={pending !== null}
        title={`Make a live change as ${accountName}?`}
        message={`You're acting as ${accountName}. This change will immediately affect ${accountName}.`}
        confirmLabel="Make live change"
        confirmColor="warning"
        onConfirm={() => settle(true)}
        onClose={() => settle(false)}
      />
    </>
  );
};
